import { ShieldCheck, CircleNotch } from "@phosphor-icons/react";

export const VERDICT_META = {
  high: { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "High confidence" },
  moderate: { cls: "bg-amber-100 text-amber-900 border-amber-300", label: "Moderate confidence" },
  low: { cls: "bg-red-100 text-red-800 border-red-300", label: "Low confidence" },
};

const SUPPORT_META = {
  supported: { cls: "text-emerald-700", label: "Supported" },
  partial: { cls: "text-amber-700", label: "Partial / general practice" },
  unsupported: { cls: "text-red-700", label: "Unsupported" },
};

export function GroundingBadge({ check, testid }) {
  if (!check) return null;
  const m = VERDICT_META[check.verdict] || {};
  return (
    <span className={`text-[10px] border px-1.5 py-0.5 ${m.cls || ""}`} data-testid={testid}>
      {check.confidence}% · {m.label || "Checked"}
    </span>
  );
}

export function VerifyGroundingButton({ onClick, busy, checked, disabled, testid, title }) {
  return (
    <button type="button" onClick={onClick} disabled={busy || disabled}
      className="text-[11px] inline-flex items-center gap-1 border border-border px-2 py-0.5 hover:bg-card disabled:opacity-50"
      data-testid={testid}
      title={title || "Reverse-check this AI output against its source material (1 AI credit)"}>
      {busy ? <CircleNotch size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
      {checked ? "Re-check grounding" : "Verify grounding"}
    </button>
  );
}

export function GroundingReport({ check, history, testid }) {
  if (!check) return null;
  const trend = (history || []).filter((h) => typeof h?.confidence === "number");
  return (
    <div className="border border-border bg-card/70 p-2.5 text-xs" data-testid={testid}>
      <div className="flex items-center gap-3 mb-1.5 flex-wrap">
        <span className="font-semibold">Grounding check</span>
        <span className="text-emerald-700">✔ {check.supported} supported</span>
        <span className="text-amber-700">◐ {check.partial} partial</span>
        <span className="text-red-700">✘ {check.unsupported} unsupported</span>
        {check.source_chars === 0 && <span className="text-red-700">· no source found</span>}
      </div>
      {trend.length > 1 && (
        <div className="flex items-center gap-1 mb-1.5 flex-wrap text-[11px]" data-testid="grounding-trend">
          <span className="text-muted-foreground">Confidence trend:</span>
          {trend.slice(-6).map((h, i, arr) => (
            <span key={i} className="flex items-center gap-1">
              <span className={`px-1 border ${VERDICT_META[h.verdict]?.cls || "border-border"}`}>{h.confidence}%</span>
              {i < arr.length - 1 && <span className="text-muted-foreground">→</span>}
            </span>
          ))}
        </div>
      )}
      {check.summary && <p className="text-muted-foreground mb-2">{check.summary}</p>}
      <div className="space-y-1.5">
        {(check.claims || []).map((c, i) => (
          <div key={i} className="border-l-2 pl-2 border-border">
            <div className="flex items-start gap-1.5">
              <span className={`font-semibold shrink-0 ${SUPPORT_META[c.support]?.cls || ""}`}>{SUPPORT_META[c.support]?.label || c.support}</span>
              <span>{c.claim}</span>
            </div>
            {c.evidence && <div className="text-muted-foreground italic mt-0.5">“{c.evidence}”</div>}
            {c.note && <div className="text-muted-foreground mt-0.5">{c.note}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
